import { useRef, useState } from 'react';
import { Download, Upload, FileSpreadsheet, AlertCircle, CheckCircle2, Images } from 'lucide-react';

import { api } from '@/lib/api';
import { useAuthStore } from '@/features/auth/authStore';
import { useImportWineImages } from '@/features/wines/useWineImages';
import { fmtInt } from '@/lib/format';

type ImportSummary = {
  id: number;
  status: string;
  rows_total: number;
  rows_processed: number;
  rows_failed: number;
  wines_created?: number;
  wines_updated?: number;
  observations_created?: number;
  purchases_created?: number;
  lots_created?: number;
  errors?: { row?: number; reason?: string; fatal?: string }[];
};

function downloadFile(url: string, filename: string, token: string | null) {
  fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : undefined })
    .then((r) => r.blob())
    .then((blob) => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    });
}

function ImportPanel({
  kind,
  templateUrl,
  templateName,
  uploadUrl,
  description,
}: {
  kind: 'vinos' | 'compras' | 'precios';
  templateUrl?: string;
  templateName?: string;
  uploadUrl: string;
  description: string;
}) {
  const token = useAuthStore((s) => s.token);
  const apiBase = api.defaults.baseURL!;
  const [file, setFile] = useState<File | null>(null);
  const [currency, setCurrency] = useState<'ARS' | 'USD'>('ARS');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (kind === 'precios') fd.append('currency', currency);
      const { data } = await api.post(uploadUrl, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(data.data);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'No se pudo procesar el archivo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-notion-600">{description}</p>

      {templateUrl && templateName && (
        <button
          type="button"
          onClick={() => downloadFile(`${apiBase}${templateUrl}`, templateName, token)}
          className="btn-secondary"
        >
          <Download size={14} /> Descargar plantilla
        </button>
      )}

      <form onSubmit={submit} className="card p-5 space-y-4">
        <div>
          <label className="label">Archivo Excel/CSV</label>
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            required
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm text-notion-700
                       file:mr-3 file:rounded-lg file:border-0 file:bg-notion-800 file:px-4 file:py-2
                       file:text-sm file:font-medium file:text-white hover:file:bg-notion-900"
          />
        </div>
        {kind === 'precios' && (
          <div>
            <label className="label">Moneda de los precios</label>
            <select className="input max-w-xs" value={currency} onChange={(e) => setCurrency(e.target.value as any)}>
              <option value="ARS">ARS</option>
              <option value="USD">USD</option>
            </select>
          </div>
        )}
        <button type="submit" className="btn-primary" disabled={!file || loading}>
          <Upload size={14} /> {loading ? 'Procesando…' : 'Subir y procesar'}
        </button>
      </form>

      {error && <div className="card p-4 text-sm text-notion-800 bg-notion-50">{error}</div>}

      {result && (
        <div className="card p-5 space-y-3">
          <div className="flex items-center gap-2">
            {result.rows_failed === 0
              ? <CheckCircle2 className="text-green-700" size={18} />
              : <AlertCircle className="text-amber-700" size={18} />}
            <h3 className="font-medium">Resumen</h3>
            <span className="text-xs px-2 py-0.5 rounded-full bg-notion-100 text-notion-700">{result.status}</span>
          </div>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div><span className="text-notion-500">Filas totales</span><p className="font-medium">{fmtInt(result.rows_total)}</p></div>
            <div><span className="text-notion-500">Procesadas</span><p className="font-medium text-green-700">{fmtInt(result.rows_processed)}</p></div>
            <div><span className="text-notion-500">Fallidas</span><p className="font-medium text-amber-700">{fmtInt(result.rows_failed)}</p></div>
            {kind === 'vinos' && (
              <>
                <div><span className="text-notion-500">Creados</span><p className="font-medium">{fmtInt(result.wines_created)}</p></div>
                <div><span className="text-notion-500">Actualizados</span><p className="font-medium">{fmtInt(result.wines_updated)}</p></div>
              </>
            )}
            {kind === 'compras' && (
              <>
                <div><span className="text-notion-500">Compras creadas</span><p className="font-medium">{fmtInt(result.purchases_created)}</p></div>
                <div><span className="text-notion-500">Lotes creados</span><p className="font-medium">{fmtInt(result.lots_created)}</p></div>
              </>
            )}
            {kind === 'precios' && (
              <div><span className="text-notion-500">Observaciones</span><p className="font-medium">{fmtInt(result.observations_created)}</p></div>
            )}
          </div>
          {!!result.errors?.length && (
            <details>
              <summary className="cursor-pointer text-sm text-notion-800">Ver errores ({result.errors.length})</summary>
              <ul className="mt-2 text-xs text-notion-600 space-y-1 max-h-60 overflow-auto">
                {result.errors.map((e, i) => (
                  <li key={i}>
                    {e.row ? `Fila ${e.row}: ` : ''}{e.reason ?? e.fatal}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

export function ImportPage() {
  const [tab, setTab] = useState<'vinos' | 'compras' | 'precios' | 'imagenes'>('vinos');

  return (
    <div className="p-8 max-w-3xl">
      <header className="mb-6">
        <h1 className="text-2xl font-medium flex items-center gap-2">
          <FileSpreadsheet size={22} className="text-notion-800" /> Importar desde Excel
        </h1>
        <p className="text-sm text-notion-500 mt-1">
          Cargá tu cava en tres pasos: vinos → compras (con fecha, precio y ubicación) → precios de mercado.
        </p>
      </header>

      <div className="flex gap-2 mb-4 border-b border-notion-200 overflow-x-auto">
        {(['vinos', 'compras', 'precios', 'imagenes'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition capitalize ' +
              (tab === t ? 'border-notion-800 text-notion-800' : 'border-transparent text-notion-500 hover:text-notion-800')
            }
          >{t}</button>
        ))}
      </div>

      {tab === 'vinos' && (
        <ImportPanel
          kind="vinos"
          templateUrl="/wines/import/template"
          templateName="vinacora-import-vinos-plantilla.xlsx"
          uploadUrl="/wines/import"
          description="Una fila por vino único. Si una bodega, productor, enólogo, cepa o lugar no existe, se crea. Usá la columna 'id' como tu identificador maestro (V001, V002…) — vas a referenciarlo desde los imports de Compras y Precios."
        />
      )}
      {tab === 'compras' && (
        <ImportPanel
          kind="compras"
          templateUrl="/purchases/import/template"
          templateName="vinacora-import-compras-plantilla.xlsx"
          uploadUrl="/purchases/import"
          description="Una fila por compra (o lote). Referencia al vino por su 'wine_ref' (el id que usaste en la plantilla de vinos). Cada fila crea una compra con su fecha, precio, lugar de compra, y un lote in_storage en la ubicación que indiques."
        />
      )}
      {tab === 'precios' && (
        <ImportPanel
          kind="precios"
          uploadUrl="/price-observations/import"
          description="Una fila por vino, una columna por vinatería/fuente. Identificás el vino con la columna 'id' (o 'wine_id' si lo preferís). Cada celda no vacía genera una observación de precio."
        />
      )}
      {tab === 'imagenes' && <ImagesImportPanel />}
    </div>
  );
}

function ImagesImportPanel() {
  const importer = useImportWineImages();
  const zipRef = useRef<HTMLInputElement>(null);
  const mappingRef = useRef<HTMLInputElement>(null);
  const [zip, setZip] = useState<File | null>(null);
  const [mapping, setMapping] = useState<File | null>(null);
  const [result, setResult] = useState<Awaited<ReturnType<typeof importer.mutateAsync>> | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!zip || !mapping) return;
    const res = await importer.mutateAsync({ zip, mapping });
    setResult(res);
  };

  return (
    <div className="card p-6 space-y-5">
      <div className="flex items-start gap-3">
        <Images size={20} className="text-notion-800 mt-0.5" />
        <div className="flex-1">
          <h2 className="font-medium text-notion-800">Imágenes de los vinos</h2>
          <p className="text-sm text-notion-500">
            Subí un ZIP con las fotos y un Excel/CSV con el mapeo. El Excel debe tener columnas
            <code className="text-[11px] bg-notion-50 mx-1 px-1 rounded">wine_ref</code>,
            <code className="text-[11px] bg-notion-50 mx-1 px-1 rounded">archivo</code>,
            <code className="text-[11px] bg-notion-50 mx-1 px-1 rounded">tipo</code>{' '}
            (frente / dorso / lateral / cápsula / otra).
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="label">ZIP con imágenes (jpg, png, webp, heic — máx 100 MB)</label>
          <input
            ref={zipRef}
            type="file"
            accept=".zip,application/zip"
            className="input"
            onChange={(e) => setZip(e.target.files?.[0] ?? null)}
          />
        </div>
        <div>
          <label className="label">Excel/CSV de mapeo</label>
          <input
            ref={mappingRef}
            type="file"
            accept=".xlsx,.csv"
            className="input"
            onChange={(e) => setMapping(e.target.files?.[0] ?? null)}
          />
        </div>

        <button
          type="submit"
          className="btn-primary"
          disabled={!zip || !mapping || importer.isPending}
        >
          <Upload size={14} /> {importer.isPending ? 'Procesando…' : 'Importar imágenes'}
        </button>
      </form>

      {result && result.status === 'done' && (
        <div className="border border-notion-200 rounded p-3 text-sm space-y-2">
          <div className="flex items-center gap-2 text-emerald-700">
            <CheckCircle2 size={16} /> Importación completada.
          </div>
          <p>
            <strong>{fmtInt(result.images_created ?? 0)}</strong> imágenes creadas,{' '}
            <strong>{fmtInt(result.rows_failed ?? 0)}</strong> filas con error.
          </p>
          {result.errors && result.errors.length > 0 && (
            <details>
              <summary className="text-xs text-notion-500 cursor-pointer">
                Ver errores ({result.errors.length})
              </summary>
              <ul className="mt-2 text-xs space-y-1 max-h-40 overflow-y-auto">
                {result.errors.map((err, i) => (
                  <li key={i} className="text-red-700">
                    Fila {err.row}: {err.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {result && result.status === 'error' && (
        <div className="border border-red-200 rounded p-3 text-sm flex items-start gap-2 text-red-700">
          <AlertCircle size={16} className="mt-0.5" />
          <div>
            <p className="font-medium">No se pudo procesar.</p>
            <p className="text-xs">{result.message}</p>
          </div>
        </div>
      )}
    </div>
  );
}
